import { Component, type ErrorInfo, type ReactNode } from "react";
import { Icon } from "./icons/Icon";
import { Button } from "./ui/button";
import { reportError } from "../utils/errorMonitor";
import "../styles/components/error.css";

interface Props {
  children: ReactNode;
  /** 自定义提示语（默认通用文案） */
  message?: string;
}

interface State {
  hasError: boolean;
  message?: string;
}

/**
 * 错误边界：页面级渲染错误兜底。
 * 捕获后统一走 errorMonitor 上报（含 componentStack），并给出「重试 / 返回首页」两条出路——
 * 不让用户卡在白屏。注意：事件处理器里的异常不会被边界捕获，那些走全局 unhandledrejection。
 */
export default class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, message: error.message };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("[error-boundary]", error, info.componentStack);
    reportError({
      type: "react_error_boundary",
      message: error.message,
      stack: error.stack,
      componentStack: info.componentStack ?? undefined,
      url: window.location.href,
      userAgent: navigator.userAgent,
    });
  }

  private handleReset = () => this.setState({ hasError: false, message: undefined });

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div className="cb-error">
        <div className="cb-error-icon">
          <Icon name="alert" size={26} />
        </div>
        <h2 className="cb-error-title">{this.props.message ?? "页面加载出错了"}</h2>
        <p className="cb-error-desc">
          错误详情已上报给运维。可以点「重试」重新渲染当前页；若反复出现，请返回首页并更换入口再试。
        </p>
        {this.state.message ? <code className="cb-error-code">{this.state.message}</code> : null}
        <div className="cb-error-actions">
          <Button icon="refresh" onClick={this.handleReset}>
            重试
          </Button>
          <Button variant="primary" icon="home" onClick={() => window.location.assign("/")}>
            返回首页
          </Button>
        </div>
      </div>
    );
  }
}
